import React, { useState, useEffect, useRef, JSX, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, BasePageProps, CommonPageId, clearGameEvent } from "@mental-mint/shared-ui";
import { 
  upsertLevelScoreInSaveData, 
  LevelScore, 
  EVENT_NAMES, 
  reportConversionEvent,
  getEnvironment,
  ENVIRONMENTS,
  getEmailModalVariant,
  EmailModalVariant,
  isAuthenticated,
  logger,
} from "@mental-mint/shared-utils";
import { loadWordDriftGame, saveWordDriftGame } from '../../Utils/saveManager';
import { ClipLoader } from 'react-spinners';
import { 
  GameState, 
  WordDriftPuzzle, 
  createGameState, 
  makeMove, 
  undoToWord,
  getPlayableWordInfo,
  limitWordsForDisplay,
  filterPlayableWordInfoForDisplay,
  getTodaysPuzzle,
  preloadDictionary,
  loadPuzzles,
  PlayableWordInfo,
  setCurrentPuzzle,
  getCurrentPuzzle,
  getWordDriftLevelId,
  findShortestPathAndNextWordForGameState
} from "../../WordDriftUtils";
import { clearWordCache } from '../../Store/wordCacheSlice';
import { StartOverButton } from "../../Components/StartOverButton/StartOverButton";
import { WordDisplay } from "../../Components/WordDisplay/WordDisplay";
import { TopNav } from "../../Components/TopNav";
import { MoveFeedback } from "../../Components/MoveFeedback";
import { HintTutorialOverlay } from "../../Components/HintTutorialOverlay";
import { useGameTimer } from "../../Hooks/useGameTimer";
import { useHintSystem } from "../../Hooks/useHintSystem";
import { WordDriftPageId } from "../WordDriftPageTypes";
import { playSound, SharedSoundEffect } from "@mental-mint/shared-ui";
import "./PagePlay.css";

/**
 * Props for the WordDrift PagePlay component.
 */
interface PagePlayProps extends BasePageProps {
  /** Navigation function for inter-page navigation */
  navigateToPage?: (pageId: string, navigationProps?: any) => void;
  /** Puzzle to play (optional, defaults to today's puzzle) */
  puzzle?: WordDriftPuzzle;
}

/**
 * The main game page for WordDrift - navigate from start word to goal word through conceptual connections.
 */
export const PagePlay: React.FC<PagePlayProps> = ({
  onRequestClose,
  navigateToPage,
  puzzle
}) => {
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  const game = currentGame!;
  const dispatch = useDispatch();
  
  // Get A/B test variant for email modal placement
  const [emailModalVariant, setEmailModalVariant] = useState<EmailModalVariant>(EmailModalVariant.Never);
  
  // Load email modal variant from GrowthBook
  useEffect(() => {
    const loadVariant = async () => {
      const variant = await getEmailModalVariant();
      setEmailModalVariant(variant);
    };
    loadVariant();
  }, []);
  
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [isDictionaryLoaded, setIsDictionaryLoaded] = useState(false);
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  const [screenHeight, setScreenHeight] = useState<number>(window.innerHeight);
  const [isPlayingDailyPuzzle, setIsPlayingDailyPuzzle] = useState<boolean>(false);
  
  // Move feedback data - passed to MoveFeedback component which calculates feedback internally
  const [moveFeedbackData, setMoveFeedbackData] = useState<{
    word1Lemmatized: string;
    word2Lemmatized: string;
    word1BfsDistance: number | null | undefined;
    word2BfsDistance: number | null | undefined;
  } | null>(null);
  
  // Ref to cache grayed out words per word - maps word lemma to its Set of grayed words
  const grayedOutWordsPerPageRef = useRef<Map<string, Set<string>>>(new Map());
  
  // Ref to cache page data - maps word lemma to its PlayableWordInfo
  // This prevents pages from recalculating when new words are added
  const pageDataCacheRef = useRef<Map<string, PlayableWordInfo>>(new Map());

  // Timer hook - manages countdown and time deduction
  const { isTimerActive, startTimer, deductTime } = useGameTimer(gameState, setGameState);
  
  // Listen for puzzle intro dismissed event from Redux
  const gameEvents = useSelector((state: RootState) => state.gameSession.gameEvents);
  useEffect(() => {
    const introDismissedEvent = gameEvents.find(e => e.type === 'word-drift:puzzle-intro-dismissed');
    if (introDismissedEvent) {
      logger.debug('[PagePlay] Puzzle intro dismissed event received, starting timer');
      startTimer();
      // Clear the event after handling
      dispatch(clearGameEvent(introDismissedEvent.timestamp));
    }
  }, [gameEvents, startTimer, dispatch]);
  
  // Hint system hook - manages hint clicks, BFS, tutorial modal, and stuck timer
  const {
    isHintInProgress,
    showHintTutorial,
    handleHintClick,
    handleAcceptHintTutorial,
    handleDeclineHintTutorial,
    isHintDisabled,
    getBfsData,
  } = useHintSystem(
    gameState,
    setGameState,
    grayedOutWordsPerPageRef,
    isTimerActive,
    deductTime,
    game
  );

  // Calculate current hint penalty based on hints used (starts at 30s, increases by 15s each time)
  const currentHintPenalty = gameState ? 30 + (15 * gameState.hintsUsed) : 30;
  // Undo always costs base penalty (30s)
  const undoPenalty = 30;

  // Pre-calculate all page data outside the map to avoid hooks violations
  // Use a ref-based cache so pages keep their data even when new pages are added
  // Memoize to avoid recalculation when other state changes trigger re-renders
  const pagesData = useMemo(() => {
    if (!gameState) return [];
    
    const data = gameState.path.map((gameWord, index) => {
      // For the current word (last in path), ALWAYS use fresh data from gameState
      // This ensures after undo, we get the recalculated word info
      if (index === gameState.path.length - 1) {
        pageDataCacheRef.current.set(gameWord.variations.lemmatized, gameState.currentWordPlayableInfo);
        return gameState.currentWordPlayableInfo;
      }
      
      // For previous pages, check cache
      const cached = pageDataCacheRef.current.get(gameWord.variations.lemmatized);
      if (cached) {
        return cached;
      }
      
      // If not in cache, calculate and cache it
      const wordInfo = getPlayableWordInfo(gameWord, false, gameState.goalWord);
      const limitedInfo = limitWordsForDisplay(wordInfo, null); // No bestWord for old pages
      const filtered = filterPlayableWordInfoForDisplay(limitedInfo, gameState.goalWord);
      pageDataCacheRef.current.set(gameWord.variations.lemmatized, filtered);
      return filtered;
    });
    
    // Validation: Ensure data length matches path length
    if (data.length !== gameState.path.length) {
      console.error('[PagePlay] pagesData length mismatch!', {
        pagesDataLength: data.length,
        pathLength: gameState.path.length
      });
    }
    
    // Validation: Ensure no undefined entries
    const hasUndefined = data.some((item, idx) => {
      if (!item) {
        console.error(`[PagePlay] pagesData[${idx}] is undefined for word:`, gameState.path[idx]?.word);
        return true;
      }
      return false;
    });
    
    if (hasUndefined) {
      console.error('[PagePlay] pagesData contains undefined entries');
    }
    
    return data;
  }, [gameState?.path, gameState?.currentWordPlayableInfo]);

  // Load dictionary and puzzles on mount or when puzzle changes
  useEffect(() => {
    const loadGameAssets = async () => {
      try {
        // Clear caches when loading new puzzle
        pageDataCacheRef.current.clear();
        grayedOutWordsPerPageRef.current.clear();
        
        // Clear Redux GameWord cache for new puzzle
        dispatch(clearWordCache());
        logger.debug('[PagePlay] Cleared word cache for new puzzle');
        
        // Load puzzles first (they're small and fast)
        await loadPuzzles();
        
        // Determine the puzzle to use
        const puzzleToUse = puzzle || getTodaysPuzzle();
        const isDailyPuzzle = !puzzle; // If no puzzle prop, it's the daily puzzle
        setIsPlayingDailyPuzzle(isDailyPuzzle);
        
        // Store the current puzzle in WordDriftUtils for later retrieval
        setCurrentPuzzle(puzzleToUse);
        
        // Record that the user has attempted this puzzle
        // Parse the date string properly to avoid timezone issues
        let puzzleDate: Date;
        if (puzzleToUse.date) {
          const [year, month, day] = puzzleToUse.date.split('-').map(Number);
          puzzleDate = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
        } else {
          puzzleDate = new Date();
        }
        const levelId = getWordDriftLevelId(puzzleDate);
        
        // Save initial attempt record (not won yet)
        const initialScore: LevelScore<any> = {
          levelId: levelId,
          isWon: false,
          gameMetadata: {}
        };
        const currentSave = await loadWordDriftGame();
        const updatedSave = upsertLevelScoreInSaveData(currentSave, initialScore);
        await saveWordDriftGame(updatedSave);
        logger.debug("Recorded puzzle attempt:", levelId, "for puzzle date:", puzzleToUse.date);
        
        // Small delay to let puzzle intro render before starting dictionary load
        // This prevents the heavy dictionary JSON parsing from blocking the intro UI
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Now load dictionary (this is large and will block parsing)
        await preloadDictionary();
        setIsDictionaryLoaded(true);
        
        // Initialize game state after dictionary is ready
        const initialGameState = createGameState(puzzleToUse);
        setGameState(initialGameState);
        
        // Compare puzzle shortest path with BFS result in development
        if (getEnvironment() === ENVIRONMENTS.DEVELOPMENT) {
          const bfsResult = findShortestPathAndNextWordForGameState(puzzleToUse.startWord, puzzleToUse.goalWord);
          logger.debug('=== Shortest Path Comparison ===');
          logger.debug('Puzzle shortest path:', puzzleToUse.shortestPath);
          logger.debug('BFS calculated distance:', bfsResult.distance);
          logger.debug('BFS next word:', bfsResult.nextWord);
          
          if (puzzleToUse.shortestPath) {
            const puzzlePathLength = puzzleToUse.shortestPath.length - 1; // Convert path length to move count
            const bfsPathLength = bfsResult.distance;
            if (puzzlePathLength !== bfsPathLength) {
              logger.warn(`⚠️ MISMATCH: Puzzle says ${puzzlePathLength} moves, BFS found ${bfsPathLength} moves`);
            } else {
              logger.debug('✅ Paths match!');
            }
          }
        }
        
        // Track that the daily puzzle is being displayed (conversion event)
        if (isDailyPuzzle) {
          reportConversionEvent(
            EVENT_NAMES.DISPLAY_DAILY_PUZZLES,
            { oncePerDay: true },
            "User viewing daily puzzle for first time"
          );
        }
      } catch (error) {
        logger.error('Failed to load game assets:', error);
      }
    };
    
    loadGameAssets();
  }, [puzzle]); // Re-run when puzzle prop changes (new puzzle selected)

  // Handle word selection
  const handleWordSelection = async (selectedWord: string) => {
    logger.debug('🖱️ Word selected (clicked):', selectedWord);

    // Handle some early returns
    if (!gameState) {
      logger.warn("Tried to click a word when game was not initialized, this should never happen");
      return;
    }
    if (gameState.isComplete) {
      logger.debug("Tried to click a word when game is complete, ignoring.")
      return;
    }
      
    // Track first interaction with DAILY puzzle (conversion event) - only on first move ever
    if (isPlayingDailyPuzzle && gameState.path.length === 1) {
      reportConversionEvent(
        EVENT_NAMES.INTERACT_WITH_DAILY_PUZZLES,
        { oncePerDay: true },
        "User started playing daily puzzles"
      );
    }
    
    // DEBUG: Log the wordLemmaMap to see what we have
    logger.debug('📚 Current wordLemmaMap keys:', Object.keys(gameState.currentWordPlayableInfo.wordLemmaMap));
    logger.debug('📚 Looking up clicked word in wordLemmaMap:', selectedWord);
    
    // Find the selected word in the current page
    const selectedGameWord: GameWord | undefined = gameState.currentWordPlayableInfo.wordLemmaMap[selectedWord];
    
    if (!selectedGameWord) {
      logger.error(`❌ Could not find game word for: "${selectedWord}"`, new Error(`Could not find game word for: ${selectedWord}`));
      logger.error('Available keys in wordLemmaMap:', Object.keys(gameState.currentWordPlayableInfo.wordLemmaMap));
      return;
    }
    
    // DEBUG: Log what GameWord we found
    logger.debug('✅ Found GameWord for clicked word:', {
      clickedWord: selectedWord,
      gameWord: selectedGameWord,
      display: selectedGameWord.variations.display,
      lemmatized: selectedGameWord.variations.lemmatized
    });
    
    // Get pre-computed BFS data if available (eliminates UI freeze!)
    const bfsData = getBfsData();
    
    // DEBUG: Log what word we're navigating to
    logger.debug('🚀 Navigating to word:', {
      display: selectedGameWord.variations.display,
      lemmatized: selectedGameWord.variations.lemmatized
    });
    
    // Make the move! (This will load the new word's definition)
    const newGameState = makeMove(gameState!, selectedGameWord, bfsData);
    
    // Play victory sound if won
    if (newGameState.isWon) {
      playSound(SharedSoundEffect.VICTORY);
      setGameState(newGameState);
      return;
    }
    
    // Calculate and show move feedback (skip first move)
    const pathLength = newGameState.path.length;
    if (pathLength >= 2) {
      const previousWord = newGameState.path[pathLength - 2].variations.lemmatized;
      const currentWord = newGameState.path[pathLength - 1].variations.lemmatized;
      const previousBfsDistance = gameState.currentPathDistanceToGoal;
      const currentBfsDistance = newGameState.currentPathDistanceToGoal;
      
      // Pass raw word data to MoveFeedback - it will calculate feedback internally
      setMoveFeedbackData({
        word1Lemmatized: previousWord,
        word2Lemmatized: currentWord,
        word1BfsDistance: previousBfsDistance,
        word2BfsDistance: currentBfsDistance
      });
    }
    
    setGameState(newGameState);
  };

  // Helper function to calculate blended percentage (cosine + BFS)
  // IMPORTANT: This is the single source of truth for percentage calculations
  // Memoized callback for move feedback completion
  const handleMoveFeedbackComplete = useCallback(() => {
    // Delay clearing feedback to allow exit animation to complete (300ms)
    setTimeout(() => {
      setMoveFeedbackData(null);
    }, 300);
  }, []);

  // Handle window resize to update screen dimensions
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      setScreenHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Navigate to post-game when game completes
  useEffect(() => {
    if (!gameState || !gameState.isComplete) return;

    const handleGameCompletion = async () => {
      logger.debug("Game is complete!");
      
      // Get the current puzzle from WordDriftUtils
      const currentPuzzle = await getCurrentPuzzle();
      if (!currentPuzzle) {
        logger.error("No active puzzle found when trying to save completion");
        return;
      }
      
      // Parse the date string properly to avoid timezone issues
      let puzzleDate: Date;
      if (currentPuzzle.date) {
        const [year, month, day] = currentPuzzle.date.split('-').map(Number);
        puzzleDate = new Date(year, month - 1, day);
      } else {
        puzzleDate = new Date();
      }
      const levelId = getWordDriftLevelId(puzzleDate);
      
      // Limit path to 15 words to avoid unbounded storage
      const MAX_PATH_LENGTH = 15;
      const truncatedPath = gameState.path.length > MAX_PATH_LENGTH
        ? gameState.path.slice(0, MAX_PATH_LENGTH)
        : gameState.path;
      
      const tentativeNewScore: LevelScore<any> = {
        levelId: levelId,
        isWon: gameState.isWon,
        usedHintTillWon: gameState.hintsUsed > 0,
        gameMetadata: {
          pathLength: gameState.path.length, // Store actual length
          optimalPathLength: gameState.optimalPath?.length || 0,
          moveCount: gameState.moveCount,
          hintsUsed: gameState.hintsUsed,
          path: truncatedPath, // Store truncated path
        }
      };
      
      const currentSave = await loadWordDriftGame();
      const updatedSave = upsertLevelScoreInSaveData(currentSave, tentativeNewScore);
      await saveWordDriftGame(updatedSave);
      logger.debug("Saved completion data:", tentativeNewScore, "for puzzle date:", currentPuzzle.date);
      
      // Track puzzle completion (conversion event)
      reportConversionEvent(
        EVENT_NAMES.COMPLETE_DAILY_PUZZLES,
        { 
          levelId, 
          oncePerLevel: true,
          metadata: { won: gameState.isWon } 
        },
        gameState.isWon ? 'won' : 'lost'
      );
      
      // A/B test: Show login modal on top of postgame for 'on_daily_puzzles_completion' variant
      if (emailModalVariant === EmailModalVariant.OnDailyPuzzlesCompletion && !isAuthenticated() && navigateToPage) {
        logger.info('[A/B Test] Showing postgame with login modal stacked on top (variant: on_daily_puzzles_completion)');
        // Navigate to PostGame first (background modal)
        navigateToPage(WordDriftPageId.PostGame, { gameState: gameState });
        // Then navigate to Login with stacking enabled (appears on top)
        navigateToPage(CommonPageId.Login, {}, true); // allowStacking = true
        return;
      }
      
      setTimeout(() => {
        navigateToPage?.(WordDriftPageId.PostGame, { gameState: gameState });
      }, 200);
    };

    handleGameCompletion();
  }, [gameState?.isComplete]);

  // Handle flip animation completion
  const handleFlipComplete = async () => {
    // Game completion is now handled by the useEffect above
    // This function is just here for the flip animation callback
  };

  // Handle start over
  const handleStartOver = async () => {
    if (isDictionaryLoaded) {
      const currentPuzzle = await getCurrentPuzzle();
      if (currentPuzzle) {
        // Clear all caches before creating new game state
        pageDataCacheRef.current.clear();
        grayedOutWordsPerPageRef.current.clear();
        
        // Small delay to allow state updates to flush
        await new Promise(resolve => setTimeout(resolve, 50));
        
        setGameState(createGameState(currentPuzzle));
      }
    }
  };
  
  // Handle help button click - show puzzle intro modal
  const handleHelpClick = () => {
    if (!navigateToPage) return;
    
    // Navigate to puzzle intro with current puzzle (use puzzle prop if available)
    navigateToPage(WordDriftPageId.PuzzleIntro, { puzzle });
  };
  
  // Handle undo button click - deduct time and update state
  const handleUndo = () => {
    if (!gameState || gameState.path.length <= 1) {
      return;
    }
    
    // Get the word to undo to (second-to-last in path)
    const targetGameWord = gameState.path[gameState.path.length - 2];
    
    // Store current word (last in path) - use lemmatized for embeddings
    const currentWord = gameState.path[gameState.path.length - 1].variations.lemmatized;
    const currentBfsDistance = gameState.currentPathDistanceToGoal;
    
    // Preview the undo state to get target BFS distance
    const previewUndoState = undoToWord(gameState, targetGameWord);
    const targetBfsDistance = previewUndoState.currentPathDistanceToGoal;
    const targetWord = targetGameWord.variations.lemmatized; // Use lemmatized form for embeddings
    
    // Pass raw word data to MoveFeedback - it will calculate feedback internally
    setMoveFeedbackData({
      word1Lemmatized: currentWord,
      word2Lemmatized: targetWord,
      word1BfsDistance: currentBfsDistance,
      word2BfsDistance: targetBfsDistance
    });
    
    // Deduct time and then apply undo in the same state update
    deductTime(undoPenalty, (stateAfterTime) => undoToWord(stateAfterTime, targetGameWord));
  };
  


  const renderDictionaryPageContents = (wordInfo: PlayableWordInfo, wordLemma: string): JSX.Element => {
    if (gameState === null) { return (<></>); }
    if (!wordInfo) {
      console.error('[PagePlay] renderDictionaryPageContents called with undefined wordInfo for:', wordLemma);
      return (<></>);
    }

    // Get grayed out words for this specific page from cache
    const pageGrayedWords = grayedOutWordsPerPageRef.current.get(wordLemma) || new Set<string>();

    return (
      <div 
        className="page-content"
        style={{
          padding: "1rem",
          backgroundColor: game.colorUiBackground,
          paddingBottom: "5rem",
          overflow: "visible",
          position: "relative"
        }}
      >
        <WordDisplay
          wordInfo={wordInfo}
          gameState={gameState}
          gameColor={game.colorPrimary}
          onWordSelection={handleWordSelection}
          grayedOutWords={pageGrayedWords}
        />
      </div>
    );
  }

  const renderCurrentPage = (): JSX.Element => {
    if (gameState === null) { return (<></>); }

    // Get the current (last) word in the path
    const currentIndex = gameState.path.length - 1;
    const currentGameWord = gameState.path[currentIndex];
    const currentPageData = pagesData[currentIndex];

    if (!currentPageData) {
      console.error('[PagePlay] Missing page data for current word:', currentGameWord?.variations);
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading...</p>
        </div>
      );
    }

    // TopNav height: breadcrumb (~2.5rem) + action buttons (~3.5rem) + moves bar (~2rem)
    const topNavHeight = "8.5rem";

    return (
      <div className="current-page-container" style={{ 
        width: "100%",
        height: `calc(100dvh - ${topNavHeight})`,
        marginTop: topNavHeight,
        overflow: "auto",
        marginLeft: "auto",
        marginRight: "auto",
      }}>
        {renderDictionaryPageContents(currentPageData, currentGameWord.variations.lemmatized)}
        
        {gameState.isComplete && 
          <div style={{marginTop: "2rem"}}>
            {renderRestartButton()}
          </div>
        }
      </div>
    );
  };

  const renderRestartButton = (): JSX.Element => {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        marginBottom: "1rem"
      }}>
        <StartOverButton 
          onStartOver={handleStartOver}
        />
      </div>
    );
  };

  return (
    <div className="word-drift-play-container" style={{
      height: "100%",
      width: "100%",
      background: game.colorUiBackground,
      transition: "background 0.8s ease",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Move Feedback Overlay */}
      <MoveFeedback 
        word1Lemmatized={moveFeedbackData?.word1Lemmatized ?? null}
        word2Lemmatized={moveFeedbackData?.word2Lemmatized ?? null}
        word1BfsDistance={moveFeedbackData?.word1BfsDistance ?? null}
        word2BfsDistance={moveFeedbackData?.word2BfsDistance ?? null}
        goalWord={gameState?.goalWord ?? null}
        startWord={gameState?.startWord ?? null}
        optimalPath={gameState?.optimalPath}
        onComplete={handleMoveFeedbackComplete}
      />
      
      {!isDictionaryLoaded || !gameState ? (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          fontFamily: game.fontSecondary,
          fontSize: "1.2rem",
          color: game.colorPrimary,
          opacity: 0.8  // Make it subtle so puzzle intro modal is more prominent
        }}>
          <div>Loading dictionary...</div>
            <div style={{ marginTop: "1rem" }}>
            <ClipLoader 
              size={32} 
              color={game.colorPrimary} 
            />
            </div>
        </div>
      ) : (
        <>
          <TopNav
            gameState={gameState}
            gameColor={game.colorPrimary}
            onHintClick={handleHintClick}
            isHintDisabled={isHintDisabled}
            onHelpClick={handleHelpClick}
            onUndo={handleUndo}
            currentHintPenalty={currentHintPenalty}
            undoPenalty={undoPenalty}
          />
          {renderCurrentPage()}
          
          {/* Hint Tutorial Overlay - "Need a hint?" modal */}
          <HintTutorialOverlay
            show={showHintTutorial}
            gameColor={game.colorPrimary}
            onAccept={handleAcceptHintTutorial}
            onDecline={handleDeclineHintTutorial}
            timePenalty={currentHintPenalty}
          />
        </>
      )}
    </div>
  );
};

export default PagePlay;